import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Virtuoso } from 'react-virtuoso';
import { useQuery } from '@tanstack/react-query';
import { Checkbox, Popover, Table } from 'antd';
import { CheckboxGroupProps, CheckboxProps } from 'antd/es/checkbox';
import { ColumnsType } from 'antd/es/table';
import classNames from 'classnames';
import dayjs, { Dayjs } from 'dayjs';
import localeData from 'dayjs/plugin/localeData';
import weekday from 'dayjs/plugin/weekday';
import moment from 'moment';
import * as api from '@apis/paymentStatements';
import { ReactComponent as CheckedRadio } from '@assets/images/radio-checked.svg';
import { ReactComponent as EmptyRadio } from '@assets/images/radio-empty.svg';
import DataFilterPanel from '@components/DataFilterPanel/DataFilterPanel';
import PeriodMenu, { DayjsType } from '@components/PeriodMenu/PeriodMenu';
import SearchResetButtons from '@components/SearchResetButtons/SearchResetButtons';
import ExcelButton from '@components/common/ExcelButton';
import Radio from '@components/common/Radio/Radio';
import { useAuthentication } from '@hooks/authentication';
import { useViewport } from '@hooks/viewport';
import { User } from '@models/auth';
import PaymentStatement, {
  GetPaymentStatementsQuery,
  PaymentStatementMemo,
  PaymentStatementRefundStatus,
  PaymentStatusType,
  SaleType,
  SortType,
} from '@models/paymentStatement';
import { useAppUserSessionStore } from '@stores/appUserSessionStore';
import { useOkCancelDialog } from '@stores/okCancelDialogStore';
import useChangeScheduleModalStore from '@stores/useChangeScheduleModalStore';
import useProductOrderDetailModalStore from '@stores/useProductOrderDetailModalStore';
import useRefundModalStore from '@stores/useRefundModalStore';
import { isSellerOrPartner } from '@utils/common';
import {
  ALL_PARTNER_SALE_TYPES,
  getPartnerSaleTypeLabel,
  getSaleTypeQueryParams,
  PARTNER_SALE_TYPE_FILTER_OPTIONS,
  shouldShowBuyerPhone,
} from '@utils/partner-sale-type';
import { localTimeToUtcISOString } from '@utils/date';
import { combinedRefundItemsByName, getRemainPurchases } from '@utils/payment';
import './SalesHistoryPage.scss';

interface SalesHistoryPageRouterState {
  state: {
    refundRequest?: boolean;
  };
}

dayjs.extend(weekday);
dayjs.extend(localeData);

export const PARTNER_PURCHASES_QUERY_KEY = (user?: User | null) => `payment-statements-${user?.id}`;

const ScrollListHeader: React.FC<{ columns?: string[] }> = (props) => {
  if (!props.columns || props.columns.length < 1) {
    return <></>;
  }
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-around',
        backgroundColor: '#FAFAFA',
        height: '40px',
        border: '1px solid #F0F0F0',
      }}>
      {(props.columns || []).map((column: any, index: number) => {
        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 500,
              flex: 1,
              height: '100%',
              borderLeft: index < 1 ? 'none' : '1px solid rgba(0,0,0,0.10)',
            }}>
            {column}
          </div>
        );
      })}
    </div>
  );
};

const ScrollListEmptyPlaceHolder: React.FC = () => {
  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#808387',
      }}>
      검색 결과가 없습니다.
    </div>
  );
};

const SalesHistoryPage: React.FC = () => {
  const { authStore } = useAuthentication();
  const { isMobile } = useViewport();
  const refundModalStore = useRefundModalStore();
  const productOrderDetailModalStore = useProductOrderDetailModalStore();
  const changeScheduleModalStore = useChangeScheduleModalStore();
  const location = useLocation() as SalesHistoryPageRouterState;
  const sessionStore = useAppUserSessionStore();
  const dialog = useOkCancelDialog();
  const [startDate, setStartDate] = useState<Dayjs | ''>('');
  const [endDate, setEndDate] = useState<Dayjs | ''>('');
  const [selectedItemIndex, setSelectedItemIndex] = useState<number>();
  const [checkedSaleType, setCheckedSaleType] = useState<SaleType[]>([]);
  const [checkedPaymentStatus, setCheckedPaymentStatus] = useState<PaymentStatusType[]>([]);
  const [paymentStatement, setPaymentStatement] = useState<PaymentStatement[]>([]);
  const [query, setQuery] = useState<GetPaymentStatementsQuery>({
    startDate: '',
    endDate: '',
    keyword: '',
    paymentStatus: [],
    sortType: 'payment.paidAt',
    saleType: '',
    page: 0,
    size: 10,
  });
  const [pagination, setPagination] = useState({
    total: 0,
    page: 0,
    size: 0,
    hasNext: false,
    showSizeChanger: false,
  });

  const partnerPurchasesQuery = useQuery({
    queryKey: [PARTNER_PURCHASES_QUERY_KEY(authStore.user), query, refundModalStore.show],
    queryFn: () => api.getPaymentStatement(query),
  });

  const TABLE_COLUMNS: ColumnsType<PaymentStatement> = [
    {
      title: '선택',
      align: 'center',
      render: (record, _record, index) => (
        <Radio
          name="payment"
          checked={index === selectedItemIndex}
          onChange={() => setSelectedItemIndex(index)}
          checkedIcon={<CheckedRadio />}
          defaultIcon={<EmptyRadio />}
        />
      ),
      fixed: 'left',
    },
    {
      title: '결제일시',
      dataIndex: 'payment',
      render: (payment) => {
        if (!payment?.paidAt) {
          return '-';
        }
        return moment(payment?.paidAt).format('YYYY.MM.DD HH:mm');
      },
      align: 'center',
    },
    {
      title: '주문상세',
      render: (record) => {
        return (
          <button
            className="page-content__table-btn"
            onClick={() => {
              productOrderDetailModalStore.open(record);
            }}>
            보기
          </button>
        );
      },
      align: 'center',
    },
    {
      title: '상태',
      render: (data) => {
        if (!data.refund || data.statuses.refund === PaymentStatementRefundStatus.NONE) {
          return '결제완료';
        } else if (data.statuses.refund === PaymentStatementRefundStatus.REQUESTED) {
          return '환불요청';
        } else if (data.statuses.refund === PaymentStatementRefundStatus.ALL) {
          return '전체환불';
        } else if (data.statuses.refund === PaymentStatementRefundStatus.PARTIAL) {
          return '부분환불';
        } else if (data.statuses.refund === PaymentStatementRefundStatus.ACCEPTED) {
          return '환불예정';
        } else if (data.statuses.refund === PaymentStatementRefundStatus.DENIED) {
          return '환불반려';
        } else {
          return '-';
        }
      },
      align: 'center',
    },
    {
      title: '판매유형',
      render: (record) => getPartnerSaleTypeLabel(record?.product),
      align: 'center',
      hidden: !isSellerOrPartner(authStore?.user),
    },
    {
      title: '구매자',
      render: (record) => {
        return `${record?.buyer?.name}\n${shouldShowBuyerPhone(record?.product) ? `(${record?.buyer?.phone})` : ''}`;
      },
      align: 'center',
    },
    {
      title: '상품명',
      render: (record) => (
        <Popover content={record.product?.name}>
          <div
            style={{
              margin: 'auto',
              width: '300px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}>
            {record.product?.name}
          </div>
        </Popover>
      ),
      align: 'center',
    },
    {
      title: '구매옵션',
      render: (record) => {
        const purchases = record?.purchases || [];
        return (
          <Popover
            content={purchases.map((item: any) => (
              <div>{`${item?.name}(${item?.count})`}</div>
            ))}>
            <div
              style={{
                margin: 'auto',
                width: '200px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
              {purchases
                .map((item: any) => `${item?.name}(${item?.count})`)
                .filter((item: any) => item?.name !== 'discount')}
            </div>
          </Popover>
        );
      },
      align: 'center',
      hidden: !isSellerOrPartner(authStore?.user),
    },
    {
      title: '수업일시',
      dataIndex: 'plannedDates',
      render: (plannedDates) => {
        if (!plannedDates || !plannedDates.length) return '-';
        return (
          <div>
            {plannedDates?.map((plannedDate: Date) => moment(plannedDate).format('YYYY.MM.DD HH:mm')).join('\n')}
          </div>
        );
      },
      align: 'center',
    },
    {
      title: <div style={{ textAlign: 'center' }}>결제금액</div>,
      render: (record) => {
        const totalAmount = (record?.payment?.amount || 0) + (record?.payment?.membershipDiscountAmount || 0);
        return `${totalAmount.toLocaleString()}원`;
      },
      align: 'right',
    },
    {
      title: <div style={{ textAlign: 'center' }}>환불금액</div>,
      dataIndex: 'refund',
      render: (refund) => (refund?.amount ? `${refund.amount.toLocaleString()}원` : '-'),
      align: 'right',
    },
    {
      title: '입력옵션',
      render: (data: PaymentStatement) => {
        const qnaMemo = data?.memo?.qna || '-';
        return qnaMemo;
      },
      hidden: !isSellerOrPartner(authStore?.user),
    },
    {
      title: '고객 기재사항',
      render: (data: PaymentStatement) => renderMemo(data.memo),
      hidden: !isSellerOrPartner(authStore?.user),
    },
  ];
  const CLASS_TYPE_LABEL = PARTNER_SALE_TYPE_FILTER_OPTIONS;
  const PAYMENT_STATUS = [
    { label: '결제완료', value: PaymentStatementRefundStatus.NONE },
    { label: '환불요청', value: PaymentStatementRefundStatus.REQUESTED },
    { label: '환불예정', value: PaymentStatementRefundStatus.ACCEPTED },
    { label: '환불반려', value: PaymentStatementRefundStatus.DENIED },
    { label: '전체환불', value: PaymentStatementRefundStatus.ALL },
    { label: '부분환불', value: PaymentStatementRefundStatus.PARTIAL },
    { label: '환불철회', value: PaymentStatementRefundStatus.REQUEST_CANCELLED },
  ];
  const SORT_OPTIONS = [
    {
      label: '결제일순',
      value: 'payment.paidAt',
      style: { color: query.sortType === 'payment.paidAt' ? '#424242' : '#B9BBBE', fontWeight: 700 },
    },
    {
      label: '수업일순',
      value: 'plannedDates',
      style: { color: query.sortType === 'plannedDates' ? '#424242' : '#B9BBBE', fontWeight: 700 },
    },
  ];

  useEffect(() => {
    const now = localTimeToUtcISOString(new Date())!;
    sessionStore.setLastCheckDates({ paymentStatementsCheckedAt: now });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setSelectedItemIndex(undefined);
  }, [query]);

  useEffect(() => {
    if (partnerPurchasesQuery.data?.data?.page !== undefined) {
      if (isMobile) {
        setPaymentStatement((prev) => {
          if (partnerPurchasesQuery.data?.data.page !== 0) {
            return [...prev, ...(partnerPurchasesQuery.data?.data.contents || [])];
          } else {
            return partnerPurchasesQuery.data?.data.contents || [];
          }
        });
      } else {
        setPaymentStatement(partnerPurchasesQuery.data?.data.contents);
      }
      setPagination((value) => {
        const newValue = { ...value };
        newValue.page = (partnerPurchasesQuery.data?.data.page || 0) + 1;
        newValue.size = partnerPurchasesQuery.data?.data.size || 10;
        newValue.total = partnerPurchasesQuery.data?.data.total || 0;
        newValue.hasNext = partnerPurchasesQuery.data?.data.hasNext || false;
        return newValue;
      });
      localStorage.setItem('platform-payment-statements-check-date', new Date().valueOf().toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnerPurchasesQuery.data?.data]);
  useEffect(() => {
    const routerState = location?.state;

    if (routerState?.refundRequest) {
      setCheckedPaymentStatus(['requested']);
      setQuery((prev) => ({ ...prev, paymentStatus: ['requested'], page: 0 }));
    }

    window.history.replaceState({}, '');
  }, [location?.state]);

  const handleAllCheckedSaleType: CheckboxProps['onChange'] = (e) => {
    setCheckedSaleType(e.target.checked ? ALL_PARTNER_SALE_TYPES : []);
  };
  const handleAllChekcedPaymentStatus: CheckboxProps['onChange'] = (e) => {
    setCheckedPaymentStatus(e.target.checked ? PAYMENT_STATUS.map((item) => item.value) : []);
  };
  const handleSaleTypeChange: CheckboxGroupProps<SaleType>['onChange'] = (e) => {
    setCheckedSaleType(e);
  };
  const handlePaymentStatusChange: CheckboxGroupProps<PaymentStatusType>['onChange'] = (e) => {
    setCheckedPaymentStatus(e);
  };
  const handleDateRangeChange = useCallback((startDate?: DayjsType, endDate?: DayjsType) => {
    if (!startDate && !endDate) {
      setStartDate('');
      setEndDate('');
    } else {
      if (startDate) {
        setStartDate(startDate);
      }
      if (endDate) {
        setEndDate(endDate);
      }
    }
  }, []);

  const handleRefundClick = () => {
    if (
      selectedItemIndex === undefined ||
      !paymentStatement[selectedItemIndex] ||
      paymentStatement[selectedItemIndex].statuses.refund !== PaymentStatementRefundStatus.REQUESTED
    ) {
      window.alert('환불예정 처리가 불가능한 상품입니다.');
    } else {
      const data = paymentStatement[selectedItemIndex];
      const combinedRefundItems = combinedRefundItemsByName(data?.refunds);

      refundModalStore.open({
        orderId: data._id,
        productName: data.product.name,
        reason: data.memo.refundUser || '-',
        refundInfo: data.refund,
        requestAt: data.refund?.requestedAt,
        historyRefundItems: combinedRefundItems,
      });
    }
  };
  const handleChangeScheduleClick = () => {
    if (selectedItemIndex === undefined) return;
    const data = paymentStatement[selectedItemIndex];

    const combinedRefundItems = combinedRefundItemsByName(data?.refunds);
    const remainPurchases = getRemainPurchases(data.purchases, combinedRefundItems);

    changeScheduleModalStore.open({
      saleType: data?.product?.saleType as 'class' | 'commerce',
      orderId: data._id,
      productName: data.product.name,
      buyerName: data.buyer.name || '',
      purchases: data.purchases,
      refunds: data?.refunds,
      remainPurchases: remainPurchases,
      paidAmount: data.payment.amount,
      paidAt: data.payment.paidAt || new Date(),
      productId: data.product.id,
      memo: data.memo,
      classInvoiceId: data.product.classInvoiceId,
    });
  };
  const renderMemo = (memo?: PaymentStatementMemo) => {
    const memoList = [
      { label: '결제메모', value: memo?.payment },
      { label: '환불메모', value: memo?.refundUser },
      { label: '배송메모', value: memo?.shipping },
    ];

    if (!memo) return null;

    return (
      <div>
        {memoList.map(
          (memoInfo) =>
            memoInfo.value && (
              <div key={memoInfo.label}>
                <span style={{ fontWeight: 'bold' }}>{memoInfo.label}: </span>
                <span>{memoInfo.value}</span>
              </div>
            ),
        )}
      </div>
    );
  };

  return (
    <div style={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
      <div className={classNames('page-title', isMobile && 'mobile')}>판매 내역</div>
      {!isMobile && (
        <>
          <div className="page-menu">
            {isSellerOrPartner(authStore?.user) && (
              <div className="page-menu__item">
                <div className="page-menu__label">판매유형</div>
                <Checkbox
                  className="black-checkbox"
                  checked={checkedSaleType.length === ALL_PARTNER_SALE_TYPES.length}
                  onChange={handleAllCheckedSaleType}
                  style={{ justifyContent: 'flex-start', width: '78px', marginRight: '28px', color: 'black' }}>
                  전체
                </Checkbox>
                <Checkbox.Group
                  className="black-checkbox"
                  options={CLASS_TYPE_LABEL}
                  value={checkedSaleType}
                  onChange={handleSaleTypeChange}
                />
              </div>
            )}
            {isSellerOrPartner(authStore?.user) && (
              <div className="page-menu__item">
                <div className="page-menu__label">상태</div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'flex-start',
                  }}>
                  <Checkbox
                    className="black-checkbox"
                    checked={checkedPaymentStatus.length === PAYMENT_STATUS.length}
                    onChange={handleAllChekcedPaymentStatus}
                    style={{ minWidth: '60px', justifyContent: 'flex-start', marginRight: '28px', color: 'black' }}>
                    전체
                  </Checkbox>
                  <Checkbox.Group
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                    }}
                    className="black-checkbox"
                    options={PAYMENT_STATUS}
                    value={checkedPaymentStatus}
                    onChange={handlePaymentStatusChange}
                  />
                </div>
              </div>
            )}
            <div className="page-menu__item">
              <div className="page-menu__label">기간</div>
              <PeriodMenu startDate={startDate} endDate={endDate} onChange={handleDateRangeChange} />
            </div>
            <hr className="page-menu__divider" />
            <SearchResetButtons
              onSearch={() => {
                setQuery({
                  startDate: startDate ? new Date(startDate.format('YYYY-MM-DD')) : '',
                  endDate: endDate ? new Date(endDate.format('YYYY-MM-DD')) : '',
                  keyword: query.keyword,
                  paymentStatus: checkedPaymentStatus.length === PAYMENT_STATUS.length ? [] : checkedPaymentStatus,
                  sortType: query.sortType,
                  ...getSaleTypeQueryParams(checkedSaleType),
                  page: 0,
                  size: 10,
                });
              }}
              onReset={() => {
                setQuery({
                  startDate: '',
                  endDate: '',
                  keyword: '',
                  paymentStatus: [],
                  sortType: 'payment.paidAt',
                  ...getSaleTypeQueryParams([]),
                  page: 0,
                  size: 10,
                });
                setCheckedSaleType([]);
                setCheckedPaymentStatus([]);
                setStartDate('');
                setEndDate('');
              }}
            />
          </div>
          <hr className="page-divider" />
        </>
      )}
      <div className={classNames('page-content', isMobile && 'mobile')} style={isMobile ? { flexGrow: 1 } : {}}>
        <DataFilterPanel
          dataTotalCount={pagination.total || 0}
          filterOptions={!isMobile ? SORT_OPTIONS : undefined}
          keywordPlaceholder="구매자, 상품명, 연락처"
          filterChange={(e) => setQuery((prev) => ({ ...prev, sortType: e.target.value as SortType, page: 0 }))}
          onSearch={(value) => setQuery((prev) => ({ ...prev, keyword: value, page: 0 }))}
        />
        {!isMobile && (
          <>
            <hr className="page-content__divider" />
            <div className="page-content__btn-container">
              <div className="page-content__btn-wrapper">
                {isSellerOrPartner(authStore?.user) && (
                  <div className="page-content__btn-wrapper">
                    <button
                      className="page-content__btn"
                      onClick={() => {
                        const isDisabled =
                          selectedItemIndex !== undefined
                            ? paymentStatement[selectedItemIndex]?.statuses?.refund !==
                              PaymentStatementRefundStatus.REQUESTED
                            : true;

                        if (isDisabled) {
                          dialog.open({
                            title: '환불 처리 불가',
                            content: '환불 처리가 불가능한 상품입니다.',
                            type: 'ok',
                          });

                          return;
                        }

                        handleRefundClick();
                      }}>
                      환불처리
                    </button>
                  </div>
                )}
                <button
                  className="page-content__btn"
                  onClick={() => {
                    if (selectedItemIndex === undefined) {
                      dialog.open({
                        title: '일정변경 불가',
                        content: '환불완료/승인/요청 상태의 건은 변경할 수 없습니다.',
                        type: 'ok',
                      });

                      return;
                    }
                    handleChangeScheduleClick();
                  }}>
                  일정변경
                </button>
              </div>
              <div className="page-content__btn-wrapper">
                <ExcelButton
                  prefixTitle="igogo_platform_amount"
                  label="금액포함"
                  startDate={startDate}
                  endDate={endDate}
                  getExcelFile={() => api.getSellerPurchasesExcelFile(query)}
                />
                <ExcelButton
                  prefixTitle="igogo_platform"
                  label="금액미포함"
                  startDate={startDate}
                  endDate={endDate}
                  getExcelFile={() => api.getSellerPurchasesExcelFile({ ...query, excludeAmount: true })}
                />
              </div>
            </div>
          </>
        )}
        {isMobile ? (
          <div style={{ flexGrow: 1, marginTop: '16px' }}>
            <ScrollListHeader columns={['결제일', '주문상세', '구매자']} />
            <Virtuoso
              style={{
                height: 'calc(100% - 40px)',
                border: '1px solid #f0f0f0',
              }}
              totalCount={paymentStatement.length}
              endReached={() => {
                const page = pagination.page || 0;
                const size = pagination.size || 10;

                if (pagination.hasNext) {
                  setQuery((prev) => {
                    return {
                      ...prev,
                      page: Number(page),
                      size: size,
                    };
                  });
                }
              }}
              itemContent={(index) => {
                const item = paymentStatement[index];
                const paidAt = item?.payment?.paidAt ? moment(item?.payment?.paidAt).format('YYYY.MM.DD') : '-';
                const buyer = `${item?.buyer.name}\n${shouldShowBuyerPhone(item?.product) ? `(${item?.buyer.phone})` : ''}`;
                return (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      whiteSpace: 'pre-wrap',
                      height: '50px',
                      fontSize: '12px',
                      borderTop: index !== 0 ? '1px solid #f0f0f0' : 0,
                    }}>
                    <div
                      style={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                      }}>
                      {paidAt}
                    </div>
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <button
                        className="pa-button ws hs white-gray"
                        style={{ width: '50px' }}
                        onClick={() => {
                          productOrderDetailModalStore.open(item as any);
                        }}>
                        보기
                      </button>
                    </div>
                    <div
                      style={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                      }}>
                      {buyer}
                    </div>
                  </div>
                );
              }}
              components={{
                EmptyPlaceholder: () => {
                  return <ScrollListEmptyPlaceHolder />;
                },
              }}
            />
          </div>
        ) : (
          <Table
            bordered
            dataSource={paymentStatement}
            style={{ whiteSpace: 'pre-wrap' }}
            columns={TABLE_COLUMNS}
            scroll={{ x: 'max-content' }}
            pagination={{
              current: pagination.page,
              pageSize: pagination.size || 10,
              total: pagination.total,
              showSizeChanger: false,
            }}
            onChange={(e) => {
              setQuery((prev) => ({ ...prev, page: (e.current || 1) - 1, size: pagination.size || 10 }));
            }}
          />
        )}
      </div>
    </div>
  );
};

export default SalesHistoryPage;
